import React from "react";

const CabinetRow = ({
  cab,
  index,
  country,
  shakerData,
  cabinetTypes,
  colorCodeData,
  fixedPriceItems = {},
  pvcRollingShutterSizes = {},
  profileHandlePrices = {},
  handleCabinetChange,
  handleKeyDown,
  handleDeleteCabinet,
  calculateTotal,
  calculateSqft,
  isCabinetValid,
}) => {
  // Updated classes with better mobile support
  const inputClasses = "w-full px-2 sm:px-3 py-2 sm:py-2 bg-white border border-indigo-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-gray-700 text-xs sm:text-sm";
  const selectClasses = "w-full px-2 sm:px-3 py-2 sm:py-2 bg-white border border-indigo-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-gray-700 text-xs sm:text-sm";

  const getColorPriceInfo = (colorCode) => {
    if (!colorCodeData || !colorCodeData[colorCode]) return "";
    
    const colorInfo = colorCodeData[colorCode];
    
    if (typeof colorInfo === 'object') {
      const parts = [];
      if (colorInfo.material) parts.push(`Material: ${colorInfo.material}`);
      if (colorInfo.unitCost) parts.push(`Cost: $${colorInfo.unitCost}`);
      if (colorInfo.shutterPrice) parts.push(`Shutter: $${colorInfo.shutterPrice}`);
      
      return parts.join(' | ');
    }
    
    return String(colorInfo);
  };

  // Check if this cabinet has all required fields for calculation
  const canCalculateTotal = () => {
    if (country === "Shaker") {
      return cab.type && cab.unitCost && cab.qty;
    } else {
      const isFixedPriceItem = fixedPriceItems && fixedPriceItems[cab.type];
      const isPVCRollingShutter = cab.type === "PVC Rolling Shutter";
      const isProfileHandle = cab.type === "Profile Handle";
      
      if (isPVCRollingShutter) {
        return cab.type && cab.pvcSize && cab.color && cab.qty;
      }
      if (isProfileHandle) {
        return cab.type && cab.profileHandleInches && cab.qty;
      }
      if (isFixedPriceItem) {
        return cab.type && cab.qty;
      }
      return cab.type && cab.height && cab.width && cab.color && cab.unitCost;
    }
  };

  // Check cabinet types
  const isFixedPriceItem = fixedPriceItems && fixedPriceItems[cab.type];
  const isPVCRollingShutter = cab.type === "PVC Rolling Shutter";
  const isProfileHandle = cab.type === "Profile Handle";

  return (
    <tr 
      className={`hover:bg-indigo-50 transition-colors duration-200 ${
        index % 2 === 0 ? 'bg-white' : 'bg-indigo-25'
      }`}
      data-row-index={index}
    >
      {/* Type Column - SHOW SHAKER FIRST - with increased mobile size */}
      <td className="p-2 sm:p-3 min-w-[150px] sm:min-w-[200px]">
        {country === "Shaker" ? (
          <select
            value={cab.type || ""}
            onChange={(e) => handleCabinetChange(index, "type", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`${selectClasses} text-base sm:text-sm`}
          >
            <option value="" className="text-gray-400 text-base">Select Shaker Item</option>
            {shakerData && shakerData.map((item) => (
              <option key={item.item} value={item.item} className="text-gray-700 text-base">
                {item.item} {item.unitCost}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={cab.type || ""}
            onChange={(e) => handleCabinetChange(index, "type", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`${selectClasses} text-base sm:text-sm`}
          >
            <option value="" className="text-gray-400 text-base">Select Cabinet Type</option>
            {/* Show Shaker as first option if available */}
            {shakerData && shakerData.length > 0 && (
              <optgroup label="Shaker Cabinets" className="text-base">
                {shakerData.map((item) => (
                  <option key={`shaker-${item.item}`} value={item.item} className="text-gray-700 text-base">
                    {item.item} {item.unitCost}
                  </option>
                ))}
              </optgroup>
            )}
            {/* Show other cabinet types */}
            {cabinetTypes && cabinetTypes.map((type) => (
              <option key={type} value={type} className="text-gray-700 text-base">
                {type}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* Description Column - Now Editable for both */}
      <td className="p-2 sm:p-3 min-w-[150px] sm:min-w-[200px]">
        <input
          type="text"
          value={cab.description || ""}
          onChange={(e) => handleCabinetChange(index, "description", e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className={`${inputClasses} text-base sm:text-sm`}
          placeholder="Enter description"
        />
      </td>

      {/* Dynamic Columns based on country */}
      {country === "Shaker" ? (
        <>
          {/* SHAKER CABINET FIELDS - with improved mobile sizing */}
          <td className="p-2 sm:p-3 min-w-[100px] sm:min-w-[120px]">
            <input
              type="number"
              value={cab.unitCost || ""}
              readOnly
              className={`${inputClasses} bg-indigo-50 text-gray-600 font-medium text-base sm:text-sm`}
              placeholder="Unit cost"
            />
          </td>
          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <input
              type="number"
              value={cab.qty || ""}
              onChange={(e) => handleCabinetChange(index, "qty", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${inputClasses} text-base sm:text-sm`}
              placeholder="Qty"
              min="1"
            />
          </td>
          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <input
              type="number"
              value={cab.discount || ""}
              onChange={(e) => handleCabinetChange(index, "discount", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${inputClasses} text-base sm:text-sm`}
              placeholder="%"
              min="0"
              max="100"
            />
          </td>
          <td className="p-2 sm:p-3 font-semibold text-gray-800 text-base sm:text-lg min-w-[100px] sm:min-w-[120px]">
            {canCalculateTotal() ? `$${calculateTotal(cab).toFixed(2)}` : "$0.00"}
          </td>
        </>
      ) : (
        <>
          {/* REGULAR CABINET FIELDS - with improved mobile sizing */}
          
          {/* Special Size/Inches Selection */}
          {isProfileHandle ? (
            <td className="p-2 sm:p-3 min-w-[150px] sm:min-w-[200px]" colSpan="2">
              <select
                value={cab.profileHandleInches || ""}
                onChange={(e) => handleCabinetChange(index, "profileHandleInches", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`${selectClasses} text-base sm:text-sm`}
              >
                <option value="" className="text-gray-400 text-base">Select Inches</option>
                {profileHandlePrices && Object.keys(profileHandlePrices).map((inches) => (
                  <option key={inches} value={inches} className="text-gray-700 text-base">
                    {inches}
                  </option>
                ))}
              </select>
            </td>
          ) : isPVCRollingShutter ? (
            <td className="p-2 sm:p-3 min-w-[150px] sm:min-w-[200px]" colSpan="2">
              <select
                value={cab.pvcSize || ""}
                onChange={(e) => handleCabinetChange(index, "pvcSize", e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`${selectClasses} text-base sm:text-sm`}
              >
                <option value="" className="text-gray-400 text-base">Select Size</option>
                {pvcRollingShutterSizes && Object.keys(pvcRollingShutterSizes).map((size) => (
                  <option key={size} value={size} className="text-gray-700 text-base">
                    {size} - ${pvcRollingShutterSizes[size]}
                  </option>
                ))}
              </select>
            </td>
          ) : (
            <>
              {/* Width and Height for regular cabinets */}
              <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
                <input
                  type="number"
                  value={cab.width || ""}
                  onChange={(e) => handleCabinetChange(index, "width", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={`${inputClasses} text-base sm:text-sm`}
                  placeholder="W"
                  disabled={isFixedPriceItem}
                />
              </td>
              <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
                <input
                  type="number"
                  value={cab.height || ""}
                  onChange={(e) => handleCabinetChange(index, "height", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={`${inputClasses} text-base sm:text-sm`}
                  placeholder="H"
                  disabled={isFixedPriceItem}
                />
              </td>
            </>
          )}

          {/* Color Selection */}
          <td className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">
            <select
              value={cab.color || ""}
              onChange={(e) => handleCabinetChange(index, "color", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${selectClasses} text-base sm:text-sm`}
              disabled={(isFixedPriceItem && !isPVCRollingShutter) || isProfileHandle}
            >
              <option value="" className="text-gray-400 text-base">Color</option>
              {isPVCRollingShutter ? (
                // PVC Rolling Shutter specific colors
                <>
                  <option value="Aluminium" className="text-gray-700 text-base">Aluminium</option>
                  <option value="Black" className="text-gray-700 text-base">Black</option>
                  <option value="White" className="text-gray-700 text-base">White</option>
                </>
              ) : (
                // Regular colors for other cabinets
                colorCodeData && Object.keys(colorCodeData).map((colorCode) => (
                  <option key={colorCode} value={colorCode} className="text-gray-700 text-base">
                    {colorCode} 
                  </option>
                ))
              )}
            </select>
          </td>

          {/* Material (Read-only) */}
          <td className="p-2 sm:p-3 min-w-[100px] sm:min-w-[120px]">
            <input
              type="text"
              value={cab.material || ""}
              readOnly
              className={`${inputClasses} bg-indigo-50 text-gray-600 text-base sm:text-sm`}
              placeholder="Material"
            />
          </td>

          {/* Square Footage */}
          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <div className="text-center">
              <div className="text-sm sm:text-base font-semibold text-gray-700">
                {isFixedPriceItem || isPVCRollingShutter || isProfileHandle ? "N/A" : `${calculateSqft(cab).toFixed(2)}`}
              </div>
            </div>
          </td>

          {/* Drawer Slides & Hinges */}
          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <input
              type="number"
              value={cab.drawerSlides || ""}
              onChange={(e) => handleCabinetChange(index, "drawerSlides", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${inputClasses} text-base sm:text-sm`}
              placeholder="DS"
              min="0"
              disabled={isFixedPriceItem || isPVCRollingShutter || isProfileHandle}
            />
          </td>

          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <input
              type="number"
              value={cab.hinges || ""}
              onChange={(e) => handleCabinetChange(index, "hinges", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${inputClasses} text-base sm:text-sm`}
              placeholder="H"
              min="0"
              disabled={isFixedPriceItem || isPVCRollingShutter || isProfileHandle}
            />
          </td>

          {/* Discount */}
          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <input
              type="number"
              value={cab.discount || ""}
              onChange={(e) => handleCabinetChange(index, "discount", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${inputClasses} text-base sm:text-sm`}
              placeholder="%"
              min="0"
              max="100"
            />
          </td>

          {/* Quantity */}
          <td className="p-2 sm:p-3 min-w-[80px] sm:min-w-[100px]">
            <input
              type="number"
              value={cab.qty || ""}
              onChange={(e) => handleCabinetChange(index, "qty", e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`${inputClasses} text-base sm:text-sm`}
              placeholder="Qty"
              min="1"
            />
          </td>

          {/* Total Price */}
          <td className="p-2 sm:p-3 font-semibold text-gray-800 text-base sm:text-lg min-w-[100px] sm:min-w-[120px]">
            {canCalculateTotal() ? `$${calculateTotal(cab).toFixed(2)}` : "$0.00"}
          </td>
        </>
      )}

      {/* Action Column - Common for both */}
      <td className="p-2 sm:p-3 min-w-[60px] sm:min-w-[80px]">
        {index > 0 && (
          <button
            onClick={() => handleDeleteCabinet(index)}
            className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-2 sm:p-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 w-10 h-10 sm:w-10 sm:h-10 flex items-center justify-center font-bold text-xl sm:text-2xl mx-auto"
            title="Delete Row"
          >
            ×
          </button>
        )}
      </td>
    </tr>
  );
};

export default CabinetRow;